import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import NoteCard from '../components/NoteCard';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiUser, FiUsers, FiClock, FiUserPlus, FiPlus, FiAlertTriangle, FiArchive } = FiIcons;

const Dashboard = () => {
  const { members, notes, getRecentNotes, searchMembers, loading, showArchived, toggleShowArchived } = useData();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showNeedsAttention, setShowNeedsAttention] = useState(false);
  const searchRef = useRef(null);
  const recentNotes = getRecentNotes(5);

  // Get active members
  const activeMembers = members.filter(m => !m.is_archived);
  
  // Get archived members count
  const archivedMembersCount = members.filter(m => m.is_archived).length;

  // Get members who need attention (no sessions or last session > 30 days ago)
  const membersNeedingAttention = activeMembers.filter(m => 
    !m.last_session || new Date(m.last_session) < new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  ).sort((a, b) => {
    // Sort by last_session date, nulls first
    if (!a.last_session && !b.last_session) return 0;
    if (!a.last_session) return -1;
    if (!b.last_session) return 1;
    return new Date(a.last_session) - new Date(b.last_session);
  });

  // Search members when query changes
  useEffect(() => {
    if (searchQuery.length > 0) {
      setSearchResults(searchMembers(searchQuery));
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchQuery, searchMembers]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchRef]);

  // Handle member selection
  const handleSelectMember = (memberId) => {
    navigate(`/members/${memberId}`);
    setSearchQuery('');
    setShowResults(false);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  // Stats for dashboard
  const stats = [
    {
      label: 'Active Members',
      value: activeMembers.length,
      icon: FiUsers,
      color: 'bg-blue-500'
    },
    {
      label: 'Recent Sessions',
      value: activeMembers.filter(m => m.last_session && new Date(m.last_session) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length,
      icon: FiClock,
      color: 'bg-green-500'
    },
    {
      label: 'Needs Attention',
      value: membersNeedingAttention.length,
      icon: FiAlertTriangle,
      color: 'bg-orange-500',
      onClick: () => setShowNeedsAttention(!showNeedsAttention)
    }
  ];

  return (
    <div className="p-6">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back, {user?.name}!
        </h1>
        <p className="text-gray-600">
          Here's what's happening with your members today.
        </p>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-8 relative"
        ref={searchRef}
      >
        <div className="max-w-3xl mx-auto">
          <div className="relative">
            <SafeIcon
              icon={FiSearch}
              className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6"
            />
            <input
              type="text"
              placeholder="Search for members by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => { if (searchQuery.length > 0) setShowResults(true); }}
              className="w-full pl-14 pr-5 py-4 border-2 border-gray-200 rounded-full focus:border-primary-500 focus:ring-0 focus:outline-none text-lg shadow-sm"
            />
          </div>

          {/* Search Results Dropdown */}
          <AnimatePresence>
            {showResults && searchResults.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute z-10 mt-2 w-full bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto"
              >
                {searchResults.map((member) => (
                  <div
                    key={member.id}
                    onClick={() => handleSelectMember(member.id)}
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <SafeIcon icon={member.is_archived ? FiArchive : FiUser} className="w-5 h-5 text-primary-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {member.name}
                          {member.is_archived && (
                            <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
                              Archived
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">{member.email}</p>
                      </div>
                      <div className="text-right text-xs">
                        <p className="text-gray-500">Last Session:</p>
                        <p className="font-medium text-gray-700">
                          {formatDate(member.last_session)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.1 }}
            className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${
              stat.label === 'Needs Attention' ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
            }`}
            onClick={stat.onClick}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                <SafeIcon icon={stat.icon} className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Archive Status Indicator */}
      {archivedMembersCount > 0 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex justify-between items-center">
            <div className="flex items-center">
              <SafeIcon icon={FiArchive} className="w-5 h-5 text-amber-600 mr-2" />
              <span className="text-amber-800">
                You have {archivedMembersCount} archived member{archivedMembersCount !== 1 ? 's' : ''}
              </span>
            </div>
            <button 
              onClick={toggleShowArchived}
              className="px-3 py-1 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-md text-sm font-medium transition-colors"
            >
              {showArchived ? "Hide Archived" : "Show Archived"}
            </button>
          </div>
        </motion.div>
      )}

      {/* Members Needing Attention Section */}
      <AnimatePresence>
        {showNeedsAttention && membersNeedingAttention.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8 overflow-hidden"
          >
            <div className="bg-orange-50 border border-orange-200 rounded-lg overflow-hidden">
              <div className="p-4 bg-orange-100 border-b border-orange-200 flex justify-between items-center">
                <div className="flex items-center">
                  <SafeIcon icon={FiAlertTriangle} className="text-orange-500 w-5 h-5 mr-2" />
                  <h3 className="font-medium text-orange-800">Members Needing Attention</h3>
                </div>
                <button
                  onClick={() => setShowNeedsAttention(false)}
                  className="text-orange-700 hover:text-orange-900"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-orange-200">
                  <thead className="bg-orange-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-700 uppercase tracking-wider">Member</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-700 uppercase tracking-wider">Last Session</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-700 uppercase tracking-wider">Programs</th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-orange-700 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-orange-100">
                    {membersNeedingAttention.map((member) => (
                      <tr key={member.id} className="hover:bg-orange-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                              <SafeIcon icon={FiUser} className="h-5 w-5 text-primary-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{member.name}</div>
                              <div className="text-sm text-gray-500">{member.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-orange-700">
                            {member.last_session ? formatDate(member.last_session) : 'Never'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-wrap gap-1">
                            {member.programs && member.programs.map(program => (
                              <span
                                key={program}
                                className="px-2 py-0.5 bg-primary-50 text-primary-700 text-xs rounded-full"
                              >
                                {program}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <Link
                            to={`/members/${member.id}`}
                            className="inline-flex items-center px-3 py-1 border border-orange-300 text-sm leading-5 font-medium rounded-md text-orange-700 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8"
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/add-member"
            className="flex items-center space-x-3 p-4 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors group"
          >
            <SafeIcon icon={FiUserPlus} className="w-6 h-6 text-primary-600" />
            <div>
              <p className="font-medium text-primary-700">Add New Member</p>
              <p className="text-sm text-primary-600">Register a new BPA member</p>
            </div>
          </Link>
          <Link
            to="/members"
            className="flex items-center space-x-3 p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors group"
          >
            <SafeIcon icon={FiUsers} className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-medium text-green-700">View All Members</p>
              <p className="text-sm text-green-600">Browse member profiles</p>
            </div>
          </Link>
          <Link
            to="/manage-coaches"
            className="flex items-center space-x-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors group"
          >
            <SafeIcon icon={FiUser} className="w-6 h-6 text-blue-600" />
            <div>
              <p className="font-medium text-blue-700">Manage Coaches</p>
              <p className="text-sm text-blue-600">Add or edit BPA coaches</p>
            </div>
          </Link>
        </div>
      </motion.div>

      {/* Recent Notes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Recent Sessions</h2>
          <Link to="/members" className="text-primary-600 hover:text-primary-700 font-medium text-sm">
            View all →
          </Link>
        </div>
        <div className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : recentNotes.length > 0 ? (
            recentNotes.map((note) => (
              <NoteCard key={note.id} note={note} showMember={true} />
            ))
          ) : (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <SafeIcon icon={FiClock} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No recent sessions recorded yet.</p>
              <Link
                to="/add-member"
                className="inline-flex items-center space-x-2 mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Add First Member</span>
              </Link>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;